/** A covalent bond's order: single to sextuple (ADR 0024: orders above three are metal–metal bonds). */
export type BondOrder = 1 | 2 | 3 | 4 | 5 | 6;
// `ring-${n}` is a plain n-membered saturated ring (the 3-8 presets and the custom-size field);
// `benzene` the aromatic 6-ring; `cycle-${id}` a named unsaturated template (see CYCLE_TEMPLATES
// in ./ringTemplates); `element-${z}` arms a periodic-table symbol -- clicking an atom sets it
// to that element (#255); `shape-${kind}` drags out a decorative shape and `curved-arrow-${head}`
// drags a mechanism arrow; `reaction-arrow-${kind}` drags a scheme arrow as canvas decoration.
// `r-group-${label}` (#648, Markush mode only) labels a terminal atom as that Markush R-group.
// `atom-list-${z,z,…}` (#654, Markush mode only) makes a clicked atom a variable heteroatom.
// `group-${key}` (#736) puts a functional group's atoms on a clicked atom, or alone on empty paper.
// Arrow tools stay armed, and a click places a default-length arrow (#434). `text` (#756) stays
// armed too: a click starts or opens a standalone text annotation for inline editing.
export type EditorTool =
  | 'draw'
  | 'text'
  | `callout-${string}`
  | 'select'
  | 'pan'
  | 'chain'
  | 'charge-apply'
  | `ring-${number}`
  | 'benzene'
  | `cycle-${string}`
  | `element-${number}`
  | `r-group-${string}`
  | `atom-list-${string}`
  | `group-${string}`
  | `shape-${string}`
  | `curved-arrow-${string}`
  | `reaction-arrow-${string}`;

// UI action rules (docs/ui-action-rules.md), Rule 3: a bond tool is *armed* on the left rail,
// then acted out on the drawingPaper -- clicking empty space / a node draws a new bond of that
// kind, clicking nearer an existing bond's midpoint than either end retypes it. `reverse` acts
// only on an existing bond. ADR 0022: `ionic`, `hydrogen` and `wavy` bonds; the old
// `clear-stereo` tool is gone -- Single / Double / Triple clear a marking instead. Rule 2: the launch default is
// `{ kind: 'order', order: 1 }` and nothing silently resets it thereafter.
export type ArmedBondTool =
  | { readonly kind: 'order'; readonly order: BondOrder }
  | { readonly kind: 'wedge'; readonly style: 'solid' | 'hashed' }
  | { readonly kind: 'crossed-double' }
  | { readonly kind: 'dative' }
  | { readonly kind: 'ionic' }
  | { readonly kind: 'hydrogen' }
  | { readonly kind: 'wavy' }
  // ADR 0026 D2: drawn from a metal into a ring or onto a bond (or onto selected atoms).
  | { readonly kind: 'hapto' }
  // ADR 0026 D6: drawn from a C–H bond's hydrogen to a metal.
  | { readonly kind: 'agostic' }
  | { readonly kind: 'reverse' };

export const DEFAULT_ARMED_BOND_TOOL: ArmedBondTool = { kind: 'order', order: 1 };

export function armedBondToolsEqual(a: ArmedBondTool, b: ArmedBondTool): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'order' && b.kind === 'order') return a.order === b.order;
  if (a.kind === 'wedge' && b.kind === 'wedge') return a.style === b.style;
  return true;
}

export interface EditorSelection {
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
  readonly annotationIds?: readonly string[];
}

export function emptySelection(): EditorSelection {
  return { atomIds: [], bondIds: [] };
}

export function hasSelection(selection: EditorSelection): boolean {
  return selection.atomIds.length > 0 || selection.bondIds.length > 0 || (selection.annotationIds?.length ?? 0) > 0;
}
